// Proactive offline prefetch: at login (and periodically while online) warm
// the SAME last-known-good cache keys each screen reads, so any tab a role can
// open works offline even if it was never visited this session.
import { cachePut, cacheGet, safeQuery, safeAll } from "@/lib/offline";
import { isUrgent } from "@/lib/urgent";

// Warm full detail snapshots for the bell's urgent items so tapping an alert
// offline opens the real record. Called from prefetch AND every bell refresh,
// so items that arrive mid-session (realtime) are cached too.
export async function prefetchUrgentDetails(supabase, uid, targets) {
  if (!targets?.length) return;
  const ids = targets.map((s) => s.id);
  const parentIds = [...new Set(targets.map((s) => s.parent_submission_id).filter(Boolean))];
  const [fullRes, cmRes, rpRes, soRes, faRes, kidsRes, parentRes] = await safeAll([
    supabase.from("submissions").select("*").in("id", ids),
    supabase.from("submission_comments").select("*").in("submission_id", ids).order("created_at"),
    supabase.from("submission_review_photos").select("*").in("submission_id", ids).order("created_at"),
    supabase.from("contractor_signoffs").select("*").in("submission_id", ids).order("created_at"),
    supabase.from("form_assignments").select("*, assignment_signoffs(*)").in("submission_id", ids),
    supabase.from("submissions").select("id,template_title,template_type,status,worker_name,created_at,link_context,parent_submission_id")
      .in("parent_submission_id", ids).order("created_at"),
    parentIds.length
      ? supabase.from("submissions").select("id,template_title").in("id", parentIds)
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (fullRes.error) return;
  const by = (rows, k) => (rows || []).reduce((m, r) => { (m[r[k]] = m[r[k]] || []).push(r); return m; }, {});
  const cms = by(cmRes.data, "submission_id"), rps = by(rpRes.data, "submission_id");
  const sos = by(soRes.data, "submission_id"), fas = by(faRes.data, "submission_id");
  const kids = by(kidsRes.data, "parent_submission_id");
  const parents = Object.fromEntries((parentRes.data || []).map((p) => [p.id, p]));
  (fullRes.data || []).forEach((s) => {
    const prev = cacheGet(uid, `subdetail:${s.id}`);
    cachePut(uid, `subdetail:${s.id}`, {
      sub: s, audit: prev?.audit || [], comments: cms[s.id] || [], children: kids[s.id] || [],
      reviewPhotos: rps[s.id] || [], signoffs: sos[s.id] || [], assignment: fas[s.id]?.[0] || null,
      parent: s.parent_submission_id ? parents[s.parent_submission_id] || null : null,
    });
  });
}

export async function prefetchOfflineData(supabase, { uid, role, activeCompanyId, isSuperAdmin }) {
  const jobs = [];

  // Form picker + fill context (all roles) — mirrors EmployeeHome's "home" key,
  // which Forms and FillForm use as their offline fallback source.
  jobs.push((async () => {
    const [tplRes, subRes, locRes, slRes] = await safeAll([
      supabase.from("templates").select("*").eq("status", "published")
        .or(activeCompanyId ? `company_id.is.null,company_id.eq.${activeCompanyId}` : "company_id.is.null"),
      activeCompanyId
        ? supabase.from("submissions").select("id,template_title,location_name,worker_name,status,flagged,revision,created_at")
            .eq("submitted_by", uid).eq("company_id", activeCompanyId).order("created_at", { ascending: false }).limit(5)
        : supabase.from("submissions").select("id,template_title,location_name,worker_name,status,flagged,revision,created_at")
            .eq("submitted_by", uid).order("created_at", { ascending: false }).limit(5),
      activeCompanyId
        ? supabase.from("locations").select("id,name,address").eq("active", true).eq("company_id", activeCompanyId).order("name")
        : Promise.resolve({ data: [], error: null }),
      supabase.from("select_lists").select("*")
        .or(activeCompanyId ? `company_id.is.null,company_id.eq.${activeCompanyId}` : "company_id.is.null"),
    ]);
    if (tplRes.error || locRes.error) return;
    // F5(a): never overwrite good cached data with an empty array on a partial
    // failure — keep the prior value for any sub-query that errored.
    const prevHome = cacheGet(uid, "home") || {};
    cachePut(uid, "home", {
      templates: tplRes.data || prevHome.templates || [],
      mySubs: !subRes.error && subRes.data ? subRes.data : (prevHome.mySubs || []),
      locations: locRes.data || prevHome.locations || [],
      lists: !slRes.error && slRes.data ? slRes.data : (prevHome.lists || []),
    });
  })());

  // Offline-first (F1): seed the in-form worker roster so a never-visited form
  // with a worker field still fills in airplane mode.
  if (activeCompanyId) {
    jobs.push((async () => {
      const { data, error } = await safeQuery(supabase.rpc("company_worker_names", { company: activeCompanyId }));
      if (!error && data) {
        const ctx = cacheGet(uid, "fill_ctx") || {};
        cachePut(uid, "fill_ctx", { ...ctx, workerNames: data });
      }
    })());
  }

  // Resources library (all roles)
  jobs.push((async () => {
    let q = supabase.from("resources").select("*").order("created_at", { ascending: false });
    q = activeCompanyId ? q.or(`company_id.is.null,company_id.eq.${activeCompanyId}`) : q.is("company_id", null);
    const { data, error } = await safeQuery(q);
    if (!error) cachePut(uid, "resources", { items: data || [] });
  })());

  if (["supervisor", "org_admin"].includes(role) || isSuperAdmin) {
    jobs.push((async () => {
      const [wRes, lRes, tRes, rRes] = await safeAll([
        supabase.from("workers").select("id,full_name,job_role").eq("status", "active")
          .eq("company_id", activeCompanyId).not("profile_id", "is", null).order("full_name"),
        supabase.from("locations").select("id,name").eq("active", true).eq("company_id", activeCompanyId).order("name"),
        supabase.from("templates").select("id,title").eq("status", "published")
          .or(`company_id.is.null,company_id.eq.${activeCompanyId}`).order("title"),
        supabase.from("resources").select("id,title,version,company_id").order("title"),
      ]);
      if (wRes.error) return;
      // F2 / F5(a): merge per field — never overwrite a good cached list (e.g.
      // documents) with an empty array because one sub-query failed.
      const prevPk = cacheGet(uid, "pickers") || {};
      cachePut(uid, "pickers", {
        workers: wRes.data || prevPk.workers || [],
        locations: !lRes.error && lRes.data ? lRes.data : (prevPk.locations || []),
        templates: !tRes.error && tRes.data ? tRes.data : (prevPk.templates || []),
        ackResources: !rRes.error && rRes.data ? rRes.data : (prevPk.ackResources || []),
      });
    })());
    jobs.push((async () => {
      // Tenant isolation (Sweep A3): the offline Submissions snapshot must match
      // the live page's scope — acting company for org roles, platform-wide for
      // Super-Admins (their deliberate platform view).
      const subQ = supabase.from("submissions")
        .select("id,template_title,template_type,template_version,location_name,worker_name,status,flagged,flag_reasons,revision,created_at,completed_at,source,contractor_names,contractor_company")
        .order("created_at", { ascending: false }).limit(300);
      const faQ = supabase.from("form_assignments").select("*, assignment_signoffs(*)")
        .order("created_at", { ascending: false }).limit(300);
      const [subRes, faRes] = await safeAll([
        isSuperAdmin ? subQ : subQ.eq("company_id", activeCompanyId),
        isSuperAdmin ? faQ : faQ.eq("company_id", activeCompanyId),
      ]);
      if (subRes.error || faRes.error) return;
      cachePut(uid, "submissions", { rows: subRes.data || [], assignments: faRes.data || [] });
    })());
  }

  if (["supervisor", "org_admin"].includes(role) && activeCompanyId) {
    // Warm the bell's urgent items in full, so tapping an alert in airplane
    // mode opens the actual record — not a spinner (same keys SubmissionDetail reads).
    jobs.push((async () => {
      const { data: urgent, error } = await safeQuery(supabase.from("submissions")
        .select("id,template_title,template_type,worker_name,location_name,created_at,flagged,flag_reasons,link_context,parent_submission_id")
        .eq("company_id", activeCompanyId).eq("status", "pending")
        .order("created_at", { ascending: false }).limit(100));
      if (error || !urgent) return;
      const urgentAll = urgent.filter(isUrgent);
      // Queue list snapshot so the Urgent Items page works even if never visited.
      cachePut(uid, "urgent_queue", urgentAll);
      // F3: warm details for the FULL queue the page renders (not just 15), so
      // no queue row is a dead tap offline. ~2.8KB median/detail on staging, so
      // the whole set is well under the localStorage budget (cachePut fails safe).
      await prefetchUrgentDetails(supabase, uid, urgentAll);
    })());
    jobs.push((async () => {
      const { data, error } = await safeQuery(supabase.rpc("my_crew_data", { p_company: activeCompanyId }));
      if (!error && data) cachePut(uid, "mycrew", data);
    })());
    jobs.push((async () => {
      const { data: wk, error: wErr } = await safeQuery(supabase.from("workers").select("id")
        .eq("profile_id", uid).eq("company_id", activeCompanyId).limit(1));
      const wid = wk?.[0]?.id;
      if (wErr || !wid) return;
      const [faRes, soRes] = await safeAll([
        supabase.from("form_assignments").select("*").eq("worker_id", wid).eq("status", "assigned").order("due_date"),
        supabase.from("assignment_signoffs").select("*, form_assignments(*)")
          .eq("worker_id", wid).eq("status", "pending").order("created_at"),
      ]);
      if (faRes.error || soRes.error) return;
      const all = soRes.data || [];
      cachePut(uid, "tasks", {
        tasks: faRes.data || [],
        signRequests: all.filter((s) => s.signer_role === "additional" && s.form_assignments && !["completed", "superseded"].includes(s.form_assignments.status)),
        mySignoffs: Object.fromEntries(all.filter((s) => s.signer_role === "assignee").map((s) => [s.assignment_id, s])),
      });
    })());
  }

  if (role === "org_admin" && activeCompanyId) {
    jobs.push((async () => {
      const [csRes, tplRes, cfgRes, tokRes, locRes] = await safeAll([
        supabase.from("contractors").select("*").eq("company_id", activeCompanyId).order("name"),
        supabase.from("templates").select("id,title,type,company_id").eq("status", "published")
          .or(`company_id.is.null,company_id.eq.${activeCompanyId}`).order("title"),
        supabase.from("contractor_form_settings").select("*").eq("company_id", activeCompanyId),
        supabase.from("contractor_portal_tokens").select("*").eq("company_id", activeCompanyId).eq("active", true).order("created_at"),
        supabase.from("locations").select("id,name").eq("active", true).eq("company_id", activeCompanyId).order("name"),
      ]);
      if (csRes.error || tplRes.error) return;
      // F5(a): preserve good cached sub-lists on partial failure.
      const prevC = cacheGet(uid, "contractors") || {};
      cachePut(uid, "contractors", {
        contractors: csRes.data || prevC.contractors || [],
        templates: (tplRes.data || []).filter((t) => t.company_id === activeCompanyId || t.company_id === null),
        settings: !cfgRes.error && cfgRes.data ? Object.fromEntries(cfgRes.data.map((r) => [r.template_id, r])) : (prevC.settings || {}),
        tokens: !tokRes.error && tokRes.data ? tokRes.data : (prevC.tokens || []),
        locations: !locRes.error && locRes.data ? locRes.data : (prevC.locations || []),
      });
    })());
    jobs.push((async () => {
      const { data: d, error } = await safeQuery(supabase.rpc("analytics_data", {
        p_company: activeCompanyId, p_from: null, p_to: null, p_location: null,
      }));
      if (!error && d) cachePut(uid, "analytics", { data: d, locOptions: (d?.locations || []).slice().sort() });
    })());
    jobs.push((async () => {
      const { data, error } = await safeQuery(supabase.from("audit_log")
        .select("id,action,details,actor,created_at")
        .eq("company_id", activeCompanyId).order("created_at", { ascending: false }).limit(30));
      if (error) return;
      const rows = data || [];
      const ids = [...new Set(rows.map((r) => r.actor).filter(Boolean))];
      let actors = {};
      if (ids.length) {
        const { data: profs } = await safeQuery(supabase.from("profiles").select("id,full_name,email").in("id", ids));
        actors = Object.fromEntries((profs || []).map((p) => [p.id, p.full_name || p.email]));
      }
      cachePut(uid, "activity", { rows, actors });
    })());
  }

  if (isSuperAdmin) {
    jobs.push((async () => {
      const [tRes, slRes, asnRes] = await safeAll([
        supabase.from("templates").select("*").order("created_at", { ascending: true }),
        supabase.from("select_lists").select("*").order("name", { ascending: true }),
        supabase.from("template_assignments").select("template_id,company_id"),
      ]);
      if (tRes.error) return;
      const counts = {};
      (asnRes.data || []).forEach((a) => { counts[a.template_id] = (counts[a.template_id] || 0) + 1; });
      cachePut(uid, "templates_admin", { rows: tRes.data || [], lists: slRes.data || [], counts });
    })());
  }

  await Promise.allSettled(jobs);
}
